<script setup lang="ts">
import { computed } from 'vue'

import type { Stat } from '@collector/sportsapi-types'

import CommonStatisticInput from './CommonStatisticInput.vue'

export type Props = {
  dirty?: boolean
  disabled?: boolean
  size?: 'sm' | 'md'
  decimalPlaces?: number
}

const props = withDefaults(defineProps<Props>(), { size: 'md' })

// state
const stat = defineModel<Stat>({ required: true })

// data
const MAX_INTEGER_DIGITS = 2

// computed
const decimalPlaces = computed(() => props.decimalPlaces ?? 2)

const maxlength = computed(() => MAX_INTEGER_DIGITS + 1 + decimalPlaces.value)

// methods
function getDecimalInputPattern(places: number): RegExp {
  return new RegExp(`^(|\\d{1,${MAX_INTEGER_DIGITS}}\\.(\\d{0,${places}})?)$`)
}

function isValidDecimalInput(value: string): boolean {
  return getDecimalInputPattern(decimalPlaces.value).test(value)
}

function handleBlur({ event }: { event: Event }): void {
  let value = String((event.target as HTMLInputElement).value)

  const endsWithDot = /^\d+\.$/.test(value)
  if (endsWithDot) {
    value += '0'.repeat(decimalPlaces.value)
  }

  if (!isValidDecimalInput(value) || endsWithDot || /^\d+$/.test(value)) {
    const normalized = Number(value)
    stat.value = {
      ...stat.value,
      value: isNaN(normalized) ? '' : normalized.toFixed(decimalPlaces.value),
    }
  }
}
</script>

<template>
  <CommonStatisticInput
    v-model="stat"
    :dirty
    :disabled
    :size
    :maxlength
    numeric
    :pattern="getDecimalInputPattern(decimalPlaces)"
    @blur="handleBlur"
  />
</template>
